import { useState, useEffect } from 'react';
import { MenuItem, Box, Grid } from '@mui/material';
import {
  DialogContainer,
  DialogTitle,
  DialogBody,
  DialogActions,
  CustomTextField,
  CustomSelect,
  CustomTextArea,
} from '../../../../../common/components';
import {
  INITIAL_TASK_STATE,
  SEVERITY_OPTIONS,
  PHASE_OPTIONS,
} from '../../constants/tasksConstants';
import { validateTaskData } from '../../utils/tasksUtils';

/**
 * Diálogo para crear/editar tareas individuales
 * Severidad y Fase solo se muestran para plantillas de tipo 'quality'
 */
const TaskDialog = ({
  open,
  onClose,
  onSave,
  selectedTask,
  editMode,
  templateId,
  templateType,
  existingTasks = [],
}) => {
  const [formData, setFormData] = useState(INITIAL_TASK_STATE);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Determinar si es plantilla de calidad (quality)
  const isQualityTemplate = templateType === 'quality';

  // Inicializar formulario
  useEffect(() => {
    if (open) {
      if (editMode && selectedTask) {
        setFormData({
          name: selectedTask.name || '',
          description: selectedTask.description || '',
          phase: selectedTask.phase || '',
          sequence_order: selectedTask.sequence_order || 1,
          max_percentage: selectedTask.max_percentage || 0,
          default_percentage: selectedTask.default_percentage || 0,
          severity: selectedTask.severity || 'low',
        });
      } else {
        // Calcular automáticamente el siguiente número de secuencia
        const maxOrder = Math.max(...existingTasks.map((t) => t.sequence_order || 0), 0);
        const nextSequenceOrder = maxOrder + 1;

        const initialState = {
          ...INITIAL_TASK_STATE,
          uuid_task_template: templateId,
          sequence_order: nextSequenceOrder, // Presetear automáticamente
        };

        // Si no es plantilla de calidad, eliminar campos phase y severity
        if (!isQualityTemplate) {
          initialState.phase = '';
          initialState.severity = 'low'; // Valor por defecto para el backend
        }

        setFormData(initialState);
      }
      setErrors({});
    }
  }, [open, editMode, selectedTask, templateId, isQualityTemplate, existingTasks]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    let processedValue = value;

    // Procesar valores numéricos
    if (['sequence_order', 'max_percentage', 'default_percentage'].includes(name)) {
      processedValue = value === '' ? '' : Number(value);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: processedValue,
    }));

    // Limpiar error del campo si existe
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const handleSubmit = async () => {
    // Preparar datos según el tipo de plantilla
    let dataToValidate = { ...formData };

    // Si no es plantilla de calidad, asegurar valores por defecto para campos obligatorios del backend
    if (!isQualityTemplate) {
      dataToValidate.phase = null; // Permitir null en el backend
      dataToValidate.severity = 'low'; // Valor por defecto obligatorio
    }

    const validationErrors = validateTaskData(dataToValidate, isQualityTemplate);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      await onSave(dataToValidate);
    } catch (error) {
      console.error('Error saving task:', error);
      // Aquí podrías mostrar un mensaje de error específico
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  if (!open) return null;

  return (
    <DialogContainer open={open} onClose={handleClose} size="medium">
      <DialogTitle>{editMode ? 'Editar Tarea' : 'Nueva Tarea'}</DialogTitle>

      <DialogBody>
        <Grid container spacing={2}>
          {/* Fila 1: Nombre y Orden */}
          <Grid item xs={12} md={8}>
            <CustomTextField
              label="Nombre"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              error={!!errors.name}
              helperText={errors.name}
              required
              fullWidth
              placeholder="Ingrese el nombre de la tarea"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <CustomTextField
              label="Orden de Secuencia"
              name="sequence_order"
              type="number"
              value={formData.sequence_order}
              onChange={handleInputChange}
              error={!!errors.sequence_order}
              helperText={errors.sequence_order || (!editMode ? 'Se asigna automáticamente' : '')}
              required
              fullWidth
              disabled={!editMode}
              inputProps={{ min: 1 }}
            />
          </Grid>

          {/* Fila 2: Descripción */}
          <Grid item xs={12}>
            <CustomTextArea
              label="Descripción"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              error={!!errors.description}
              helperText={errors.description}
              multiline
              rows={3}
              fullWidth
              placeholder="Ingrese una descripción detallada de la tarea"
            />
          </Grid>

          {/* Fila 3: Fase y Severidad (Solo Quality) */}
          {isQualityTemplate && (
            <>
              <Grid item xs={12} md={6}>
                <CustomSelect
                  label="Fase"
                  name="phase"
                  value={formData.phase}
                  onChange={handleInputChange}
                  error={!!errors.phase}
                  fullWidth
                >
                  {PHASE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </CustomSelect>
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomSelect
                  label="Severidad"
                  name="severity"
                  value={formData.severity}
                  onChange={handleInputChange}
                  error={!!errors.severity}
                  required
                  fullWidth
                >
                  {SEVERITY_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </CustomSelect>
              </Grid>
            </>
          )}

          {/* Fila 4: Porcentajes */}
          <Grid item xs={12} md={6}>
            <CustomTextField
              label="Porcentaje Máximo"
              name="max_percentage"
              type="number"
              value={formData.max_percentage}
              onChange={handleInputChange}
              error={!!errors.max_percentage}
              helperText={errors.max_percentage}
              fullWidth
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              InputProps={{
                endAdornment: '%',
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CustomTextField
              label="Porcentaje por Defecto"
              name="default_percentage"
              type="number"
              value={formData.default_percentage}
              onChange={handleInputChange}
              error={!!errors.default_percentage}
              helperText={errors.default_percentage}
              fullWidth
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              InputProps={{
                endAdornment: '%',
              }}
            />
          </Grid>
        </Grid>
      </DialogBody>

      <DialogActions handleClose={handleClose} handleSave={handleSubmit} />
    </DialogContainer>
  );
};

export default TaskDialog;
